import React, { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import CustomButton from "components/common/Button";
import InlineLoader from "./InlineLoader";
import {
  getBillingChargeApproval,
  approveBillingChargeApproval,
  rejectBillingChargeApproval,
} from "services/billing";
import { hasPermission } from "hooks/usePermission";

import { CustomChip, TableComponent } from "./ui";
import {
  BsChargeApprovalDetail,
  BsChargeApprovalLineItem,
  formatDate,
  formatMoney,
  statusColor,
  titleCase,
} from "./types";

type BreakdownAmountKey = "salary" | "platformFee" | "bonus" | "raise" | "discount";

/** Bold + amber when the increment is actually present, so it stands out in review. */
function IncrementCell({ cents }: { cents?: number }) {
  if (!cents) return <>{formatMoney(0)}</>;
  return (
    <span className="para-body-s-medium" style={{ color: "#8a5300" }}>
      {formatMoney(cents)}
    </span>
  );
}

const breakdownAmount = (row: BsChargeApprovalLineItem, key: BreakdownAmountKey): number | undefined =>
  row.breakdown ? row.breakdown[key] : undefined;

const lineItemColumns = [
  { id: "description", label: "Employee / item" },
  {
    id: "salary",
    label: "Salary",
    render: (_v: unknown, row: BsChargeApprovalLineItem) => formatMoney(breakdownAmount(row, "salary") ?? 0),
  },
  {
    id: "raise",
    label: "Raise",
    render: (_v: unknown, row: BsChargeApprovalLineItem) => <IncrementCell cents={breakdownAmount(row, "raise")} />,
  },
  {
    id: "bonus",
    label: "Bonus",
    render: (_v: unknown, row: BsChargeApprovalLineItem) => <IncrementCell cents={breakdownAmount(row, "bonus")} />,
  },
  {
    id: "discount",
    label: "Discount",
    render: (_v: unknown, row: BsChargeApprovalLineItem) => formatMoney(breakdownAmount(row, "discount") ?? 0),
  },
  {
    id: "platformFee",
    label: "Platform fee",
    render: (_v: unknown, row: BsChargeApprovalLineItem) => formatMoney(breakdownAmount(row, "platformFee") ?? 0),
  },
  { id: "amountCents", label: "Total", render: (v: number) => formatMoney(v) },
];

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-025 border rounded-lg p-075" style={{ minWidth: 150 }}>
      <p className="para-body-s-medium color-text-body-secondary">{label}</p>
      <p className="para-body-m-medium color-text-body-primary">{value}</p>
    </div>
  );
}

/**
 * [PRD-3321] Manual charge approval gate — detail view.
 * Approve always releases the charge (same override the existing manual
 * collect-invoice path already uses), so a guard warning here is informational,
 * never a hard block — it gives the admin the context to decide.
 */
/** Backend errors arrive as the `error[0]?.description` array shape used app-wide. */
const errorMessage = (err: unknown): string => {
  const description = (err as Array<{ description?: string }> | undefined)?.[0]?.description;
  return description || "Something went wrong, please try again";
};

export default function ChargeApprovalDetailModal({
  approvalId,
  onClose,
  onSuccess,
  onError,
}: {
  approvalId: string;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}) {
  const canManage = hasPermission("billing.manage");
  const [detail, setDetail] = useState<BsChargeApprovalDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [showRejectReason, setShowRejectReason] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const data = await getBillingChargeApproval(approvalId);
        if (active) setDetail(data);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [approvalId]);

  const handleApprove = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await approveBillingChargeApproval(approvalId);
      onSuccess("Charge approved successfully");
      onClose();
    } catch (err) {
      onError(errorMessage(err));
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await rejectBillingChargeApproval(approvalId, reason.trim() || undefined);
      onSuccess("Charge rejected successfully");
      onClose();
    } catch (err) {
      onError(errorMessage(err));
      setSubmitting(false);
    }
  };

  const approval = detail?.approval;
  const isPending = approval?.status === "pending";

  return (
    <Dialog open maxWidth="md" fullWidth>
      <DialogTitle>
        <div className="flex justify-between items-center">
          <p className="heading-h4-semibold color-text-heading-primary">Charge approval</p>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <InlineLoader />
        ) : !detail || !approval ? (
          <p className="para-body-m-regular color-text-body-secondary">Charge approval not found.</p>
        ) : (
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap gap-075">
              <Field label="Customer" value={approval.customerName || approval.customerMonolithId || "—"} />
              <Field label="Status" value={<CustomChip label={titleCase(approval.status)} size="small" color={statusColor(approval.status)} />} />
              <Field label="Amount" value={formatMoney(approval.amountCents, approval.currency)} />
              <Field label="Invoice status" value={titleCase(detail.invoiceStatus)} />
              <Field label="Queued" value={formatDate(approval.createdAt)} />
              {approval.reviewedBy && <Field label="Reviewed by" value={approval.reviewedBy} />}
              {approval.reviewedAt && <Field label="Reviewed" value={formatDate(approval.reviewedAt)} />}
              {approval.rejectionReason && <Field label="Rejection reason" value={approval.rejectionReason} />}
            </div>

            {detail.guardWarning && (
              <div
                className="para-body-s-regular rounded-lg p-075"
                style={{ background: "#fff3e0", color: "#8a5300", border: "1px solid #ffcc80" }}
              >
                <p className="para-body-s-medium">
                  ⚠ Recurring-charge guard would normally block this charge
                </p>
                <p>
                  Customer already has a recurring charge within the last {detail.guardWarning.windowDays}{" "}
                  days: {formatMoney(detail.guardWarning.recentChargeAmountCents)} (
                  {titleCase(detail.guardWarning.recentChargeStatus)}) on{" "}
                  {formatDate(detail.guardWarning.recentChargeCreatedAt)}.
                </p>
                <p>Approving will charge anyway (admin override, same as manual invoice collection).</p>
              </div>
            )}

            <div className="flex flex-col gap-05 mt-05">
              <p className="heading-h5-medium color-text-heading-primary">Line items</p>
              <TableComponent columns={lineItemColumns} data={detail.lineItems} disableSort tableCellSmall />
            </div>

            {showRejectReason && (
              <div className="flex flex-col gap-025">
                <label className="para-body-s-medium color-text-body-primary" htmlFor="reject-reason">
                  Reason (optional)
                </label>
                <textarea
                  id="reject-reason"
                  className="border rounded-lg p-075 para-body-m-regular"
                  rows={2}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. duplicate charge, customer disputed"
                />
              </div>
            )}
          </div>
        )}
      </DialogContent>
      {canManage && isPending && (
        <DialogActions>
          {showRejectReason ? (
            <>
              <CustomButton label="Cancel" onClick={() => setShowRejectReason(false)} isDisabled={submitting} />
              <CustomButton
                label={submitting ? "Rejecting…" : "Confirm reject"}
                onClick={handleReject}
                isDisabled={submitting}
              />
            </>
          ) : (
            <>
              <CustomButton label="Reject" onClick={() => setShowRejectReason(true)} isDisabled={submitting} />
              <CustomButton
                label={submitting ? "Approving…" : "Approve"}
                onClick={handleApprove}
                isDisabled={submitting}
              />
            </>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
}
